// Analytics — stat tiles + lightweight CSS bar lists from GET /analytics.
// No chart library; bars are pure Tailwind (see ui.BarList).
import { useEffect, useState } from 'react';
import type { Analytics as AnalyticsData } from '../../lib/suite-types';
import { agencyApi } from '../../lib/agencyApi';
import { BarList, Card, EmptyNote, ErrorNote, SectionHeader, Select, Spinner, StatTile } from './ui';

const RANGES: [string, string][] = [
  ['7d', 'Last 7 days'],
  ['30d', 'Last 30 days'],
  ['90d', 'Last 90 days'],
];

export default function Analytics() {
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [range, setRange] = useState('30d');

  useEffect(() => {
    setData(null);
    setError(null);
    agencyApi
      .analytics(range)
      .then((d) => setData(d))
      .catch((e: Error) => setError(e.message));
  }, [range]);

  const totals = data?.totals;
  const nudgeRate =
    data && data.planNudges.shown > 0
      ? `${Math.round((data.planNudges.clicked / data.planNudges.shown) * 100)}%`
      : '—';
  // Cold start: a brand-new agency has no engagement yet — show a friendly note, not a wall of zeros.
  const hasActivity = !!data && (
    (totals?.widgetOpens ?? 0) + (totals?.docViews ?? 0) + (totals?.videoPlays ?? 0) + (totals?.ticketsCreated ?? 0) +
    data.toursCompleted + data.planNudges.shown > 0
  );

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Analytics"
        subtitle="How your clients engage with content, tours, and support."
        action={
          <Select value={range} onChange={setRange}>
            {RANGES.map(([id, label]) => (
              <option key={id} value={id}>{label}</option>
            ))}
          </Select>
        }
      />

      {error && <ErrorNote error={error} />}
      {!data && !error && <Spinner label="Loading analytics…" />}

      {data && !hasActivity && (
        <EmptyNote>
          No activity in this range yet. Once your support widget is live and your clients start
          opening it — viewing docs, watching videos, submitting tickets — their engagement shows up here.
        </EmptyNote>
      )}

      {data && hasActivity && (
        <>
          {/* Stat tiles */}
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <StatTile label="Widget opens" value={totals?.widgetOpens ?? 0} />
            <StatTile label="Doc views" value={totals?.docViews ?? 0} />
            <StatTile label="Video plays" value={totals?.videoPlays ?? 0} />
            <StatTile label="Tickets created" value={totals?.ticketsCreated ?? 0} />
            <StatTile label="Tours completed" value={data.toursCompleted} />
            <StatTile label="Plan nudges shown" value={data.planNudges.shown} />
            <StatTile label="Plan nudges clicked" value={data.planNudges.clicked} />
            <StatTile label="Nudge conversion" value={nudgeRate} hint="clicked ÷ shown" />
          </div>

          {/* Bar lists */}
          <div className="grid gap-5 lg:grid-cols-2">
            <Card className="p-5">
              <h3 className="mb-4 text-sm font-semibold text-slate-700">Content views by area</h3>
              <BarList items={data.contentViews.map((c) => ({ label: c.area, value: c.count }))} />
            </Card>
            <Card className="p-5">
              <h3 className="mb-4 text-sm font-semibold text-slate-700">Tickets by area</h3>
              <BarList items={data.ticketsByArea.map((t) => ({ label: t.area, value: t.count }))} />
            </Card>
            <Card className="p-5">
              <h3 className="mb-4 text-sm font-semibold text-slate-700">Tickets by assignee</h3>
              <BarList
                items={data.ticketsByAssignee.map((t) => ({ label: t.name, value: t.count }))}
                emptyLabel="No assigned tickets yet."
              />
            </Card>
            <Card className="p-5">
              <h3 className="mb-4 text-sm font-semibold text-slate-700">Top content</h3>
              <BarList items={data.topContent.map((t) => ({ label: t.title, value: t.count }))} />
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
